import { clsx } from 'clsx'
import type { ChangeEvent } from 'react'
import { toast } from 'react-hot-toast'

import type { IconProps } from '@fv/client-components'
import type { PropsWithClassName, SelectFieldOption } from '@fv/client-types'
import type { ShipmentListPreference } from '@fv/models'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'

import { useUpdateShipmentListPreference } from '../settings/user-preferences/mutations'

const viewOptions: SelectFieldOption[] = [
  { text: 'Card view', value: 'cards' },
  { text: 'Condensed view', value: 'condensed' },
  { text: 'Calendar view', value: 'calendar' },
]
const viewIcons: Record<ShipmentListPreference, IconProps['icon']> = {
  cards: 'rectangle-list',
  condensed: 'list',
  calendar: 'calendar',
}

type Props = PropsWithClassName<{
  value: ShipmentListPreference
  onChange: (value: ShipmentListPreference) => void
  selectClassName?: string
}>

export const ShipmentViewSelect = ({
  value,
  onChange,
  className,
  selectClassName,
}: Props) => {
  const mutation = useUpdateShipmentListPreference()
  const handleChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const val = event.target.value as ShipmentListPreference
    onChange(val)
    try {
      await mutation.mutateAsync(val)
      toast.success('Shipment view preference saved.')
    } catch {
      toast.error('Failed to update preference.')
    }
  }

  return (
    <div className={clsx('relative', className)}>
      <InputGroup
        className="mb-0"
        inputType="select"
        inputProps={{
          id: 'shipment-view-select',
          name: 'shipment-view-select',
          value,
          options: viewOptions,
          onChange: handleChange,
          className: selectClassName ? ` ${selectClassName}` : 'w-auto',
        }}
        startContent={
          <InputAdornment
            position="start"
            icon={viewIcons[value]}
            className="text-fv-gray-dark"
          />
        }
      />
    </div>
  )
}
